import { Action, createSlice, PayloadAction } from '@reduxjs/toolkit';

import {
  Company,
  CompanyZone,
  isPoolBrainCompany,
  isServiceTitanApiCompany,
  isServiceTitanPortalV2Company,
  isStCapacityCompany,
  PoolBrainCompany,
  ServiceTitanApiCompany,
  ServiceTitanPortalV2Company,
  StCapacityCompany,
  PointmanCompany,
  isPointmanCompany
} from '_lib/api/apiTypes.shim';

import { RootState } from '_lib/redux/store';

export type StCompanyProviderData = {
  /* ID of our integrated ST tenant. */
  originalCompanyTenantId: string;
};

export type CompanyProviderData = StCompanyProviderData | unknown;

export type CompanyContext = {
  company: Company;
  switched: boolean;
  originalCompanyName: string;
  companyProviderData?: unknown;
};

export type ZoneZipContext = {
  selectedZone: CompanyZone | null;
  selectedZipOrPostal: string | null;
  overrideZipMatch: boolean | null;
};

export type SisterCompanyContext = {
  /**
   * Cached list of full detail sisterCompanies. To be used as a convenience when full sister company is needed.
   * if it is `null` then defer to company.sisterCompanies and do a fresh GET by id
   */
  sisterCompanies: Company[];
};

export type StPortalV2CompanyContext = Omit<CompanyContext, 'company' | 'companyProviderData'> & {
  company: ServiceTitanPortalV2Company;
  companyProviderData: StCompanyProviderData;
};
export type StApiCompanyContext = Omit<CompanyContext, 'company' | 'companyProviderData'> & {
  company: ServiceTitanApiCompany;
  companyProviderData: StCompanyProviderData;
};
export type StCapacityCompanyContext = Omit<CompanyContext, 'company' | 'companyProviderData'> & {
  company: StCapacityCompany;
  companyProviderData: StCompanyProviderData;
};
export type PoolBrainCompanyContext = Omit<CompanyContext, 'company'> & {
  company: PoolBrainCompany;
};
export type PointmanCompanyContext = Omit<CompanyContext, 'company'> & {
  company: PointmanCompany;
};

export const isStPortalV2CompanyContext = (
  companyContext: CompanyContext | undefined
): companyContext is StPortalV2CompanyContext => {
  return !!companyContext && isServiceTitanPortalV2Company(companyContext.company);
};

export const isStApiCompanyContext = (
  companyContext: CompanyContext | undefined
): companyContext is StApiCompanyContext => {
  return !!companyContext && isServiceTitanApiCompany(companyContext.company);
};

export const isStCapacityCompanyContext = (
  companyContext: CompanyContext | undefined
): companyContext is StCapacityCompanyContext => {
  return !!companyContext && isStCapacityCompany(companyContext.company);
};

export const isPoolBrainCompanyContext = (
  companyContext: CompanyContext | undefined
): companyContext is PoolBrainCompanyContext => {
  return !!companyContext && isPoolBrainCompany(companyContext.company);
};

export const isPointmanCompanyContext = (
  companyContext: CompanyContext | undefined
): companyContext is PointmanCompanyContext => {
  return !!companyContext && isPointmanCompany(companyContext.company);
};

interface CompanySlice {
  /* Pane-Specific State */
  companyContext: CompanyContext | undefined;
  sisterCompanyContext: SisterCompanyContext | undefined;
  zoneZipContext: ZoneZipContext | undefined;
}

const initialState = (): CompanySlice => ({
  companyContext: undefined,
  sisterCompanyContext: undefined,
  zoneZipContext: undefined
});

type SetCompanyContextAction = PayloadAction<CompanyContext>;
type SwitchCompanyAction = PayloadAction<CompanyContext['company']>;
type SetSisterCompaniesAction = PayloadAction<SisterCompanyContext | undefined>;
type SetZoneZipContextAction = PayloadAction<ZoneZipContext | undefined>;

export const companySlice = createSlice({
  name: 'companySlice',
  initialState: initialState(),
  reducers: {
    setCompanyContext(state, action: SetCompanyContextAction) {
      state.companyContext = action.payload;
    },
    switchCompany(state, action: SwitchCompanyAction) {
      let companyProviderData: CompanyProviderData;

      if (
        (isStApiCompanyContext(state.companyContext) ||
          isStCapacityCompanyContext(state.companyContext) ||
          isStPortalV2CompanyContext(state.companyContext)) &&
        state.companyContext.companyProviderData?.originalCompanyTenantId
      ) {
        companyProviderData = {
          originalCompanyTenantId: state.companyContext.companyProviderData?.originalCompanyTenantId
        } satisfies StCompanyProviderData;
      } else if (
        isServiceTitanApiCompany(action.payload) ||
        isStCapacityCompany(action.payload) ||
        isServiceTitanPortalV2Company(action.payload)
      ) {
        companyProviderData = {
          originalCompanyTenantId: action.payload.dataProvider.providerOptions.tenantId
        } satisfies StCompanyProviderData;
      }

      const companyContext: CompanyContext = {
        company: action.payload,
        switched: true,
        originalCompanyName: state.companyContext?.originalCompanyName || action.payload.name,
        companyProviderData
      };

      state.companyContext = { ...state.companyContext, ...companyContext };
      state.sisterCompanyContext = undefined;
      state.zoneZipContext = undefined;
    },
    setSisterCompanyContext(state, action: SetSisterCompaniesAction) {
      state.sisterCompanyContext = action.payload;
    },
    setZoneZipContext(state, action: SetZoneZipContextAction) {
      state.zoneZipContext = action.payload;
    },
    reset: () => initialState()
  },
  selectors: {
    selectCompanyContext: (state) => state.companyContext,
    selectSisterCompanyContext: (state) => state.sisterCompanyContext,
    selectZoneZipContext: (state) => state.zoneZipContext
  }
});

export const { setCompanyContext, switchCompany, setSisterCompanyContext, setZoneZipContext, reset } =
  companySlice.actions;
export const { selectCompanyContext, selectSisterCompanyContext, selectZoneZipContext } = companySlice.getSelectors(
  (rootState: RootState) => rootState.companySlice
);

/**
 * @returns whether or not the action is a 'setZoneZipContext' action AND a new Zone is selected for a zoned company
 */
export const isNewZoneSelectedPredicate = (
  action: Action,
  currentState: RootState,
  originalState: RootState
): action is SetZoneZipContextAction =>
  setZoneZipContext.match(action) &&
  !!(
    currentState.companySlice.companyContext?.company.isZoned &&
    currentState.companySlice.zoneZipContext?.selectedZone &&
    currentState.companySlice.zoneZipContext.selectedZone.id !==
      originalState.companySlice.zoneZipContext?.selectedZone?.id
  );
